import React from 'react'
import { updateRoute } from '../route'
import { FormattedMessage } from 'react-intl'
import { SourceName } from '.'
import {
  ControlsNoData,
  ControlsWithData,
  LegacyGrid,
  SortConfig
} from '../../legacy-design-system/legacy-grid'
import { LegacyEmptyState } from '../../legacy-design-system/legacy-empty-state'
import {
  SourceNameCell,
  IpAddressCell,
  StartDateCell,
  EndDateCell,
  LastModifiedCell,
  ActionsCell
} from './cell-content'

const routeToAddSourceNamePage = (organizationID: string) => {
  updateRoute(`/${organizationID}/rules/ip/source-names/add`)
}

export interface SourceNamesManagerViewProps {
  readonly loading: boolean
  readonly refetch: () => Promise<any>
  readonly organizationID: string
  readonly sourceNames: SourceName[]
  readonly onSort: (sortConfig: SortConfig<SourceName>) => void
  readonly onSearch: (value: string) => void
  readonly noData: boolean
  readonly activeSortConfigs: Array<SortConfig<SourceName>>
}

export function SourceNamesManagerView({
  loading,
  refetch,
  organizationID,
  sourceNames,
  onSort,
  onSearch,
  noData,
  activeSortConfigs
}: SourceNamesManagerViewProps) {
  return (
    <React.Fragment>
      {noData === true ? (
        <ControlsNoData
          description={{
            id: 'detail_ipsManagement_sourceNamesDefinition'
          }}
          addBtnTitle={{
            id: 'action_ipsManagement_addSourceName'
          }}
          addBtnDataE2e={'route-to-add-source-name-form'}
          onClickAdd={() => routeToAddSourceNamePage(organizationID)}
        />
      ) : (
        <ControlsWithData
          description={{
            id: 'detail_ipsManagement_sourceNamesDefinition'
          }}
          addBtnTitle={{
            id: 'action_ipsManagement_addSourceName'
          }}
          addBtnDataE2e={'route-to-add-source-name-form'}
          onClickAdd={() => routeToAddSourceNamePage(organizationID)}
          onSearch={onSearch}
          searchPlaceholder={{
            id: 'title_ipsManagement_ipSourceNames_searchPlaceholder'
          }}
        />
      )}
      {noData === true && (
        <LegacyEmptyState
          children={
            <FormattedMessage id="action_ipsManagement_noSourceNamesAdded" />
          }
        />
      )}
      {noData === false && (
        <LegacyGrid<SourceName>
          data-testid="source-names-manager-grid"
          onSort={onSort}
          loading={loading}
          rowData={sourceNames}
          emptyGridMessageConfig={{
            id: 'detail_ipsManagement_noSourceNamesFound'
          }}
          columnConfigs={[
            {
              styles: {
                maxWidth: '300px'
              },
              heading: {
                id: 'title_ipsManagement_ipSourceNames_gridColumnHeader_sourceName'
              },
              sorting: {
                field: 'name'
              },
              CellContent: ({ rowData }) => (
                <SourceNameCell
                  organizationID={organizationID}
                  rowData={rowData}
                />
              )
            },
            {
              styles: {
                maxWidth: '300px'
              },
              heading: {
                id: 'title_ipsManagement_ipSourceNames_gridColumnHeader_ipAddress'
              },
              sorting: {
                field: 'cidrBlocks'
              },
              CellContent: ({ rowData: { cidrBlocks } }) => (
                <IpAddressCell rowData={{ cidrBlocks }} />
              )
            },
            {
              styles: {
                maxWidth: '160px'
              },
              heading: {
                id: 'title_start_dateRange'
              },
              sorting: {
                field: 'start',
                getValue: ({ start }) => new Date(start).getTime()
              },
              CellContent: ({ rowData: { start } }) => (
                <StartDateCell rowData={{ start }} />
              )
            },
            {
              styles: {
                maxWidth: '160px'
              },
              heading: {
                id: 'title_end_dateRange'
              },
              sorting: {
                field: 'expiration',
                getValue: (sourceName) =>
                  !!sourceName.expiration
                    ? new Date(sourceName.expiration).getTime()
                    : Infinity
              },
              CellContent: ({ rowData: { expiration } }) => (
                <EndDateCell rowData={{ expiration }} />
              )
            },
            {
              styles: {
                maxWidth: '160px'
              },
              heading: {
                id: 'title_ipsManagement_ipSourceNames_gridColumnHeader_lastDateEdited'
              },
              sorting: {
                field: 'modified',
                getValue: ({ modified }) => new Date(modified).getTime()
              },
              CellContent: ({ rowData: { modified } }) => (
                <LastModifiedCell rowData={{ modified }} />
              )
            },
            {
              styles: {
                width: '33px',
                padding: '0'
              },
              CellContent: ({ rowData: { id, name } }) => (
                <ActionsCell
                  organizationID={organizationID}
                  refetch={refetch}
                  rowData={{
                    id,
                    name
                  }}
                />
              )
            }
          ]}
          activeSortConfigs={activeSortConfigs}
        />
      )}
    </React.Fragment>
  )
}
